import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, BookOpen, Users, Shield, AlertTriangle } from 'lucide-react';
import OrganigrammeTab from '../features/governance/org/OrganigrammeTab';

interface GouvernanceCEModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: 'ce' | 'sa';
}

type GouvernanceCETabId =
  | 'esprit'
  | 'organigramme'
  | 'cadre'
  | 'maturite'
  | 'raci'
  | 'roadmap'
  | 'rituels';

export default function GouvernanceCEModal({ isOpen, onClose, variant = 'ce' }: GouvernanceCEModalProps) {
  const [activeTab, setActiveTab] = useState<GouvernanceCETabId>('esprit');
  const [activeTiDetail, setActiveTiDetail] = useState<'kpi' | 'niveaux' | 'prisme' | null>(null);

  if (!isOpen) return null;

  const tabs: { id: GouvernanceCETabId; label: string }[] = [
    { id: 'esprit', label: 'Esprit & Objectifs macro' },
    { id: 'organigramme', label: 'Organigramme' },
    { id: 'cadre', label: 'Cadre holistique' },
    { id: 'maturite', label: 'Maturité' },
    { id: 'raci', label: 'Rôles & responsabilités' },
    { id: 'roadmap', label: 'Plan TactiCE 2026' },
    { id: 'rituels', label: 'Gouvernance & rituels' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-linear-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/40 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-indigo-500/40 bg-slate-950/70 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-indigo-500/20 border border-indigo-400/70 flex items-center justify-center shadow-lg">
                <Shield className="w-6 h-6 text-indigo-200" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-indigo-300 font-semibold">
                  Esprit & Objectifs Macro
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  Gouvernance CE
                </h2>
                <p className="text-xs md:text-sm text-indigo-100/80 mt-1">
                  La plus haute gouvernance opérationnelle de BZ : aligner stratégie, TI, humains et argent des clients investis en TI.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="ml-4 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/20 p-2 text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-4 border-b border-indigo-500/30 bg-slate-950/60">
            <div className="flex justify-center">
              <div className="inline-flex rounded-xl border border-indigo-500/40 bg-slate-900/60 p-1 text-xs md:text-sm">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-semibold transition ${
                      activeTab === tab.id
                        ? 'bg-indigo-500/40 text-white border border-indigo-300/80 shadow-sm'
                        : 'text-indigo-100/80 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 text-sm md:text-[0.9rem] text-slate-100">
            {activeTab === 'esprit' && <EspritCETab setActiveTiDetail={setActiveTiDetail} variant={variant} />}

            {activeTab === 'organigramme' && <OrganigrammeTab />}

            {activeTab === 'raci' && <RaciCETab />}

            {activeTab !== 'esprit' && activeTab !== 'organigramme' && activeTab !== 'raci' && (
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-lg">
                  <p className="text-indigo-100 mb-2 font-semibold">
                    Ces onglets seront construits après la validation de l&apos;esprit, de l&apos;organigramme et des
                    objectifs macro du CE.
                  </p>
                  <p className="text-slate-300 text-sm">
                    On commence par clarifier la mission, les rôles, la structure et la posture du CE. Le reste (cadre holistique,
                    maturité, roadmap, rituels) viendra s&apos;ancrer sur cette base.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Détail TI qui rapporte au client */}
          <AnimatePresence>
            {activeTiDetail && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.8 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-black/70"
                  onClick={() => setActiveTiDetail(null)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.94, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.94, y: 10 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="max-w-5xl w-full bg-slate-950 border border-emerald-500/60 rounded-2xl shadow-2xl p-6 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold mb-1">
                          TI qui rapporte au client
                        </p>
                        <h3 className="text-lg font-bold text-white">
                          {activeTiDetail === 'kpi' && 'Parler KPI / SMART / ROI comme nos clients'}
                          {activeTiDetail === 'niveaux' && 'Voir chaque décision en Stratégique / Tactique / Opérationnel'}
                          {activeTiDetail === 'prisme' && 'Adoption PRIsME par le CE'}
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTiDetail(null)}
                        className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3 text-sm text-slate-100/90">
                      {activeTiDetail === 'kpi' && (
                        <>
                          <p>
                            Le CE a la responsabilité de maîtriser et de coacher les concepts clés de PRIsME pour que les
                            gestionnaires puissent ensuite les transmettre à leurs équipes, puis au client. On parle d&apos;un
                            langage commun de performance, de gouvernance, de valeur d&apos;affaires et d&apos;humain.
                          </p>
                          <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">
                            Concepts à maîtriser et à coacher (référence : page Concepts)
                          </p>
                          <div className="grid md:grid-cols-3 gap-4 text-xs">
                            <div className="space-y-1.5">
                              <p className="font-semibold text-emerald-200">Stratégique</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                <li>Cadres Référentiels</li>
                                <li>KPI &amp; Indicateurs de Performance</li>
                                <li>Alignement Business–TI</li>
                                <li>Gouvernance (Complet)</li>
                                <li>Cascade Stratégique–Tactique–Opérationnelle</li>
                              </ul>
                            </div>
                            <div className="space-y-1.5">
                              <p className="font-semibold text-emerald-200">Tactique</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                <li>Concept maturité (ASIS&nbsp;(Actuel) – Target&nbsp;(Cible))</li>
                                <li>Roadmap Annuel</li>
                                <li>PRIsME Master → PRIsME dérivés</li>
                                <li>IA Éthique &amp; Responsable</li>
                                <li>Boucles d&apos;apprentissage rapides (Kaizen, Agile)</li>
                                <li>Flow visuel &amp; décisionnel</li>
                              </ul>
                            </div>
                            <div className="space-y-1.5">
                              <p className="font-semibold text-emerald-200">Opérationnel</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                <li>Arbre PRIsME tronc–branches–feuilles</li>
                                <li>Niveaux de qualité &amp; maturité 1→5</li>
                                <li>Cadre de compétence employé</li>
                                <li>Gouvernance RACI</li>
                                <li>Magie de l&apos;IA BZ (Triptyque)</li>
                                <li>Sécurité &amp; conformité by design</li>
                                <li>Leadership collaboratif &amp; excellence opérationnelle</li>
                              </ul>
                            </div>
                          </div>
                        </>
                      )}

                      {activeTiDetail === 'niveaux' && (
                        <p>
                          Une décision uniquement &laquo; stratégique &raquo; ne suffit jamais. Le CE valide systématiquement
                          comment une décision se décline en tactique (organisation, priorités) et en opérationnel (ce qui
                          change demain matin pour les équipes). Notre taille (50–60 personnes) nous oblige à soutenir les trois
                          niveaux, pas seulement la slide PowerPoint.
                        </p>
                      )}

                      {activeTiDetail === 'prisme' && (
                        <p>
                          PRIsME, avec son arbre, ses branches et ses feuilles, est la vue d&apos;ensemble de BZ. Quand les leaders
                          du CE l&apos;adoptent vraiment et l&apos;alimentent eux-mêmes, ils multiplient les chances que les
                          gestionnaires et leurs équipes l&apos;adoptent à leur tour. PRIsME devient alors l&apos;outil commun qui
                          forme, structure et outille tout le monde – et l&apos;IA de BZ que nous entraînons se nourrit directement
                          de cette discipline.
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function EspritCETab({
  setActiveTiDetail,
  variant,
}: {
  setActiveTiDetail: (id: 'kpi' | 'niveaux' | 'prisme' | null) => void;
  variant: 'ce' | 'sa';
}) {
  const isSA = variant === 'sa';

  return (
    <div className="space-y-8">
      {/* 1. Esprit du CE – en un coup d’œil */}
      <section className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-2 bg-slate-900/80 border border-indigo-500/60 rounded-xl p-5 space-y-3">
          <h3 className="flex items-center gap-2 text-lg font-bold text-indigo-100">
            <BookOpen className="w-5 h-5" />
            {isSA ? 'Esprit du département de Solutions d\'affaires' : 'Esprit du Dany CA/CE'}
          </h3>
          <ul className="list-disc list-inside text-slate-100/90 text-[0.9rem] space-y-1.5">
            {isSA ? (
              <>
                <li>Transformer les besoins d’affaires en résultats concrets, mesurables et durables.</li>
                <li>Positionner les TI, la cybersécurité et l’IA comme des leviers de croissance et non comme des centres de coûts.</li>
                <li>Accompagner chaque client avec une approche humaine, structurée et adaptée à sa réalité.</li>
                <li>Simplifier la complexité pour aider les dirigeants à prendre de meilleures décisions.</li>
                <li>Développer une gouvernance vivante, pragmatique et alignée aux objectifs d’affaires.</li>
                <li>Créer une relation de partenariat stratégique à long terme avec chaque client.</li>
              </>
            ) : (
              <>
                <li>Transformer chaque dollar TI investi par le client en valeur mesurable.</li>
                <li>Prêcher par l&apos;exemple : gouvernance simple, cohérente, respectée.</li>
                <li>Montrer la voie sur l&apos;IA pour BZ d&apos;abord, puis pour nos clients.</li>
                <li>Faire sentir chaque client et chaque employé unique, jamais un numéro, avec un service adapté à ses vrais besoins.</li>
              </>
            )}
          </ul>
        </div>

        <div className="bg-slate-900/80 border border-emerald-500/60 rounded-xl p-5 flex flex-col justify-between">
          <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold mb-2">
            Focus client
          </p>
          <p className="text-slate-100/90 text-sm mb-3">
            Le CE protège une idée simple : les TI ne sont pas un coût, mais un investissement qui doit rapporter pour chaque client, selon sa réalité.
          </p>
          <p className="text-xs text-emerald-200/90 font-semibold">
            Règle CE : aucune décision ne doit améliorer BZ au détriment du client.
          </p>
        </div>

        <div className="bg-slate-900/80 border border-purple-500/60 rounded-xl p-5 flex flex-col justify-between">
          <p className="text-xs uppercase tracking-wide text-purple-300 font-semibold mb-2">
            Révolution IA
          </p>
          <p className="text-slate-100/90 text-sm mb-3">
            BZ devient son propre laboratoire IA : on structure, on teste, on apprend ici avant d&apos;accompagner nos clients. L&apos;IA nous permet d&apos;aller plus en profondeur dans la compréhension des besoins, sans perdre le côté humain.
          </p>
          <p className="text-xs text-purple-200/90 font-semibold">
            L&apos;IA est un levier de gouvernance, de personnalisation et de rentabilité, pas un gadget.
          </p>
        </div>
      </section>

      {/* 2. Les 3 pôles de leadership – très visuel */}
      <section className="space-y-4">
        <h3 className="flex items-center gap-2 text-lg font-bold text-indigo-200">
          <Users className="w-5 h-5" />
          Les 3 pôles du CE
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          {/* François */}
          <div className="bg-slate-900/70 border border-emerald-500/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-3">
              <img src="/FP.png" alt="François Delisle" className="w-10 h-10 rounded-full border-2 border-emerald-400" />
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">François Delisle</p>
                <p className="text-[0.7rem] text-emerald-100/80">VP Gouvernance corporative</p>
              </div>
            </div>
            <ul className="list-disc list-inside text-slate-100/85 text-[0.85rem] space-y-1">
              <li>Protège les acquis, la gouvernance corporative, la conformité et la valeur de BZ.</li>
              <li>Fait vivre la gouvernance, pas juste l&apos;écrire.</li>
              <li>Apporte le filtre &laquo; risque, conformité & gouvernance &raquo; à chaque décision CE.</li>
            </ul>
          </div>

          {/* Dany */}
          <div className="bg-slate-900/70 border border-indigo-500/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-3">
              <img src="/DB.png" alt="Dany Bourassa" className="w-10 h-10 rounded-full border-2 border-indigo-400" />
              <div>
                <p className="text-xs uppercase tracking-wide text-indigo-300 font-semibold">Dany Bourassa</p>
                <p className="text-[0.7rem] text-indigo-100/80">PDG • Dany CA/CE</p>
              </div>
            </div>
            <ul className="list-disc list-inside text-slate-100/85 text-[0.85rem] space-y-1">
              <li>Responsable de l&apos;exécution globale (Service TI, Prog &amp; IA, Solutions &amp; Cyber/PRIsME, Infonuagique).</li>
              <li>Transforme l&apos;innovation et l&apos;IA en efficacité et rentabilité.</li>
              <li>Vise 100&nbsp;% de clients satisfaits et une organisation qui apprend.</li>
            </ul>
          </div>

          {/* Dany */}
          <div className="bg-slate-900/70 border border-rose-500/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-3">
              <img src="/DB.png" alt="Dany Bourassa" className="w-10 h-10 rounded-full border-2 border-rose-400" />
              <div>
                <p className="text-xs uppercase tracking-wide text-rose-300 font-semibold">Dany Bourassa</p>
                <p className="text-[0.7rem] text-rose-100/80">VP Développement Stratégique</p>
              </div>
            </div>
            <ul className="list-disc list-inside text-slate-100/85 text-[0.85rem] space-y-1">
              <li>Dirige Ventes &amp; Marketing : nouveaux clients et croissance des comptes existants.</li>
              <li>Aligne la promesse faite au client avec la capacité réelle des équipes.</li>
              <li>Suit l&apos;impact des décisions CE sur la croissance et la qualité de la relation client.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 3. Objectifs 2026 – KPIs concrets */}
      <section className="space-y-4">
        <h3 className="flex items-center gap-2 text-lg font-bold text-indigo-200">
          <Shield className="w-5 h-5" />
          Objectifs 2026 – Gouvernance CE
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          {/* KPI 1 – TI qui rapporte */}
          <div className="bg-slate-900/75 border border-emerald-500/60 rounded-xl p-4 space-y-3">
            <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">
              TI qui rapporte au client
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setActiveTiDetail('kpi')}
                className="w-full text-left px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/50 text-emerald-100 text-xs md:text-sm hover:bg-emerald-500/20 transition"
              >
                Maîtriser et coacher les concepts
              </button>
              <button
                type="button"
                onClick={() => setActiveTiDetail('niveaux')}
                className="w-full text-left px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/50 text-emerald-100 text-xs md:text-sm hover:bg-emerald-500/20 transition"
              >
                Voir chaque décision en Stratégique / Tactique / Opérationnel
              </button>
              <button
                type="button"
                onClick={() => setActiveTiDetail('prisme')}
                className="w-full text-left px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/50 text-emerald-100 text-xs md:text-sm hover:bg-emerald-500/20 transition"
              >
                Adoption PRIsME par le CE
              </button>
            </div>
          </div>

          {/* KPI 2 – Leadership & gouvernance vivante */}
          <div className="bg-slate-900/75 border border-indigo-500/60 rounded-xl p-4 space-y-2">
            <p className="text-xs uppercase tracking-wide text-indigo-300 font-semibold">
              Leadership & gouvernance vivante
            </p>
            <ul className="list-disc list-inside text-slate-100/90 text-[0.9rem] space-y-1">
              <li>100&nbsp;% des gestionnaires ont un mandat écrit (R, A, périmètre, indicateurs clés).</li>
              <li>&ge; 90&nbsp;% des décisions CE sont documentées et communiquées en &lt; 5 jours ouvrables.</li>
              <li>1 rituel CE → gestionnaires par mois pour garder la gouvernance vivante.</li>
            </ul>
          </div>

          {/* KPI 3 – IA structurée & gagnant-gagnant */}
          <div className="bg-slate-900/75 border border-purple-500/60 rounded-xl p-4 space-y-2">
            <p className="text-xs uppercase tracking-wide text-purple-300 font-semibold">
              IA structurée & gagnant-gagnant
            </p>
            <ul className="list-disc list-inside text-slate-100/90 text-[0.9rem] space-y-1">
              <li>Au moins 1 initiative IA structurée par grand département, validée par le CE.</li>
              <li>0 initiative IA qui réduit la qualité ou la confiance client (barre minimale CE).</li>
              <li>Pour chaque projet IA, un indicateur de valeur pour le client ET pour BZ est suivi.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

type RaciCode = 'R' | 'A' | 'C' | 'I';

function RaciCETab() {
  const [selectedCell, setSelectedCell] = useState<{
    domainId: string;
    domainLabel: string;
    ownerLabel: string;
    code: RaciCode;
    display: string;
  } | null>(null);

  const codeMeta: Record<
    RaciCode,
    {
      label: string;
      description: string;
      risk: string;
    }
  > = {
    R: {
      label: 'R – Responsable (job au quotidien)',
      description:
        "Pilote concret qui fait avancer la job jour après jour. Il coordonne, enlève les blocages et s'assure que \"quelqu'un s'en occupe vraiment\".",
      risk:
        'Si le R est flou ou absent, le sujet flotte : réunions où on en parle souvent, mais rien ne se matérialise vraiment sur le terrain.',
    },
    A: {
      label: 'A – Approbateur (ultime redevable)',
      description:
        'Celui qui porte la décision finale devant le CE et les clients. Il tranche en cas de conflit et protège la cohérence globale.',
      risk:
        'Si le A est multiple ou pas assumé, on tombe dans le flou politique : décisions contestées, reculs fréquents, perte de crédibilité CE.',
    },
    C: {
      label: 'C – Consulté (intelligence avant décision)',
      description:
        "On va chercher son input avant de décider. Il apporte des angles morts (terrain, client, finance, sécurité) qu'on ne veut pas découvrir après coup.",
      risk:
        'Si le C est oublié, le risque est de prendre une décision propre sur papier mais toxique dans la vraie vie (terrain, clients, finances).',
    },
    I: {
      label: 'I – Informé (cohérence après décision)',
      description:
        'Reste branché sur le sujet pour garder la vue d’ensemble et ajuster ses propres décisions en conséquence.',
      risk:
        'Si le I est mal défini, chacun réinvente sa version de la décision : rumeurs, incohérences, perte de focus dans les équipes.',
    },
  };

  const domains = [
    {
      id: 'vision-ti-ia',
      label: 'Vision TI & IA + priorisation annuelle (roadmap CE)',
      subtitle: 'Où on va et dans quel ordre on investit.',
      risks:
        "Sans vision TI/IA claire, chaque projet semble prioritaire. On dilue les efforts, on éparpille les équipes et on ne livre rien de structurant.",
      matrix: {
        fp: 'C',
        db: 'A/R',
        ym: 'C',
        ce: 'A',
      },
    },
    {
      id: 'ti-qui-rapporte',
      label: '« TI qui rapporte » : grands investissements TI clients',
      subtitle: 'S’assurer que chaque dollar TI client se traduit en valeur mesurable.',
      risks:
        'Si on perd ce filtre, la TI redevient un centre de coûts. On empile des projets « cool » mais qui ne changent rien pour les clients.',
      matrix: {
        fp: 'C',
        db: 'A/R',
        ym: 'C',
        ce: 'A',
      },
    },
    {
      id: 'risques-securite',
      label: 'Risques, conformité, sécurité (politiques, seuils, exceptions)',
      subtitle: 'Ligne de défense officielle de BZ et de nos clients.',
      risks:
        'Si le R/A n’est pas clair ici, on finit par accepter des exceptions dangereuses « pour dépanner », jusqu’au jour où ça casse vraiment.',
      matrix: {
        fp: 'A/R',
        db: 'C',
        ym: 'I',
        ce: 'A',
      },
    },
    {
      id: 'offres-pricing',
      label: 'Offres, pricing, promesse marché (clients BZ)',
      subtitle: 'Ce qu’on promet réellement au marché et à quel prix.',
      risks:
        'Si les offres et le pricing ne sont pas bien tenus, on crée un écart entre ce qui est vendu et ce que les équipes peuvent livrer.',
      matrix: {
        fp: 'I',
        db: 'C',
        ym: 'A/R',
        ce: 'A',
      },
    },
    {
      id: 'orga-ti-prisme',
      label: 'Organisation interne TI/PRIsME (organigramme, périmètres)',
      subtitle: 'Qui fait quoi, où commence et où finit chaque rôle.',
      risks:
        'Sans orga claire, on épuise les bons joueurs, on crée des trous de responsabilité et on multiplie les doubles emplois.',
      matrix: {
        fp: 'C',
        db: 'A/R',
        ym: 'C',
        ce: 'A',
      },
    },
    {
      id: 'culture-rh-leaders',
      label: 'Culture, RH leaders TI (mandats gestionnaires CE)',
      subtitle: 'Les gestionnaires CE savent-ils exactement pour quoi ils sont payés?',
      risks:
        'Si les mandats sont flous, on se retrouve avec des gestionnaires « pompiers » au lieu de leaders qui tiennent leurs résultats.',
      matrix: {
        fp: 'R',
        db: 'A',
        ym: 'C',
        ce: 'A',
      },
    },
    {
      id: 'rituels-gouvernance',
      label: 'Rituels & gouvernance CE (cadence, décisions, suivi)',
      subtitle: 'La machine de décision CE : tempo, discipline, boucles de suivi.',
      risks:
        'Sans rituels clairs, le CE devient un meeting de discussion sans mémoire ni décisions exécutées.',
      matrix: {
        fp: 'R',
        db: 'A',
        ym: 'C',
        ce: 'A',
      },
    },
    {
      id: 'ia-structuree',
      label: 'IA structurée : critères, garde-fous, go/no-go projets',
      subtitle: 'Où on met l’IA, pourquoi et avec quelles limites.',
      risks:
        'Sans garde-fous, on risque de lancer des initiatives IA qui grugent la confiance client ou qui épuisent les équipes pour peu de valeur.',
      matrix: {
        fp: 'R',
        db: 'A',
        ym: 'C',
        ce: 'A',
      },
    },
    {
      id: 'pipeline-croissance',
      label: 'Pipeline & croissance (cibles, mix de clientèle)',
      subtitle: 'Quel type de croissance on veut vraiment, et avec quels clients.',
      risks:
        'Sans arbitrage CE, on peut remplir le pipeline avec les mauvais types de clients et plomber la rentabilité et le moral.',
      matrix: {
        fp: 'I',
        db: 'C',
        ym: 'A/R',
        ce: 'A',
      },
    },
    {
      id: 'adoption-prisme',
      label: 'Adoption PRIsME (CE → gestionnaires → équipes)',
      subtitle: 'Est-ce que PRIsME vit vraiment dans la gestion quotidienne?',
      risks:
        'Si PRIsME reste un beau poster, on perd notre cerveau collectif. Chaque équipe repart avec sa méthode et l’IA BZ ne peut pas apprendre.',
      matrix: {
        fp: 'C',
        db: 'A/R',
        ym: 'R',
        ce: 'A',
      },
    },
  ] as const;

  const owners = {
    fp: 'François (VP Gouvernance corporative)',
    db: 'Dany (PDG, Dany CA/CE)',
    ym: 'Dany (Ventes & Dév. stratégique)',
    ce: 'Dany CA/CE',
  } as const;

  const handleClick = (domainId: string, domainLabel: string, ownerKey: keyof typeof owners, value: string) => {
    if (!value) return;
    const upper = value.toUpperCase();
    let code: RaciCode = 'R';
    if (upper.includes('A')) code = 'A';
    else if (upper.includes('R')) code = 'R';
    else if (upper.includes('C')) code = 'C';
    else if (upper.includes('I')) code = 'I';

    setSelectedCell({
      domainId,
      domainLabel,
      ownerLabel: owners[ownerKey],
      code,
      display: value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-300" />
          RACI CE – Vue macro (avatars)
        </h3>
        <p className="text-xs md:text-sm text-slate-200/90">
          Un seul &laquo; A &raquo; clair par domaine; le CE agit comme instance formelle, les trois pôles portent le
          &laquo; R &raquo; au quotidien.
        </p>
      </div>

      {/* Légende RACI */}
      <div className="grid md:grid-cols-4 gap-3 text-[0.75rem] md:text-xs">
        <div className="bg-slate-900/80 border border-emerald-500/60 rounded-xl p-3">
          <p className="font-semibold text-emerald-300 mb-1">R – Responsable</p>
          <p className="text-slate-200/90">
            Porte la job au quotidien, coordonne, s&apos;assure que ça avance concrètement.
          </p>
        </div>
        <div className="bg-slate-900/80 border border-indigo-500/60 rounded-xl p-3">
          <p className="font-semibold text-indigo-300 mb-1">A – Approbateur</p>
          <p className="text-slate-200/90">
            Ultime redevable, tranche en cas de blocage, valide la décision finale.
          </p>
        </div>
        <div className="bg-slate-900/80 border border-purple-500/60 rounded-xl p-3">
          <p className="font-semibold text-purple-300 mb-1">C – Consulté</p>
          <p className="text-slate-200/90">
            Donne son input avant la décision; on ne décide pas sans l&apos;avoir entendu.
          </p>
        </div>
        <div className="bg-slate-900/80 border border-slate-500/60 rounded-xl p-3">
          <p className="font-semibold text-slate-200 mb-1">I – Informé</p>
          <p className="text-slate-200/80">
            Tient la vue d&apos;ensemble; informé rapidement après la décision ou les changements majeurs.
          </p>
        </div>
      </div>

      {/* Avatars */}
      <section className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-indigo-300 font-semibold">
          Les 3 pôles + le Dany CA/CE
        </p>
        <div className="grid md:grid-cols-4 gap-4 text-xs md:text-[0.8rem]">
          <div className="bg-slate-900/70 border border-emerald-500/60 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <img src="/FP.png" alt="François Delisle" className="w-9 h-9 rounded-full border-2 border-emerald-400" />
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">François</p>
                <p className="text-[0.7rem] text-emerald-100/80">VP Gouvernance corporative</p>
              </div>
            </div>
            <p className="text-slate-200/85">
              Protège les acquis, la conformité et fait vivre les règles de jeu de la maison.
            </p>
          </div>

          <div className="bg-slate-900/70 border border-indigo-500/60 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <img src="/DB.png" alt="Dany Bourassa" className="w-9 h-9 rounded-full border-2 border-indigo-400" />
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-indigo-300 font-semibold">Dany</p>
                <p className="text-[0.7rem] text-indigo-100/80">PDG • Dany CA/CE</p>
              </div>
            </div>
            <p className="text-slate-200/85">
              Responsable de l&apos;exécution globale TI/IA/PRIsME et de la transformation des idées en résultats.
            </p>
          </div>

          <div className="bg-slate-900/70 border border-rose-500/60 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <img src="/DB.png" alt="Dany Bourassa" className="w-9 h-9 rounded-full border-2 border-rose-400" />
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-rose-300 font-semibold">Dany</p>
                <p className="text-[0.7rem] text-rose-100/80">Ventes &amp; Dév. stratégique</p>
              </div>
            </div>
            <p className="text-slate-200/85">
              Porte la promesse marché, la croissance et l&apos;alignement capacité réelle ↔ promesse client.
            </p>
          </div>

          <div className="bg-slate-900/70 border border-slate-500/60 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-indigo-500/20 border border-indigo-300 flex items-center justify-center text-xs font-semibold text-indigo-100">
                CE
              </div>
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-slate-200 font-semibold">Dany CA/CE</p>
                <p className="text-[0.7rem] text-slate-200/80">Instance formelle de décision</p>
              </div>
            </div>
            <p className="text-slate-200/85">
              Prend les décisions formelles, assume l&apos;A collectif et tient la cohérence globale.
            </p>
          </div>
        </div>
      </section>

      {/* Tableau RACI macro */}
      <section className="space-y-3">
        <p className="text-xs uppercase tracking-wide text-indigo-300 font-semibold">
          Matrice RACI – Niveau CE (macro)
        </p>
        <div className="overflow-x-auto rounded-xl border border-indigo-500/50 bg-slate-950/60">
          <table className="min-w-full text-xs md:text-[0.8rem] border-collapse">
            <thead>
              <tr className="bg-slate-900/80 text-slate-100">
                <th className="py-2 px-3 text-left font-semibold border-b border-indigo-500/40">
                  Domaine / Décision clé
                </th>
                <th className="py-2 px-3 text-center font-semibold border-b border-emerald-500/40">FP</th>
                <th className="py-2 px-3 text-center font-semibold border-b border-indigo-500/40">DB</th>
                <th className="py-2 px-3 text-center font-semibold border-b border-rose-500/40">YM</th>
                <th className="py-2 px-3 text-center font-semibold border-b border-slate-500/40">CE</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => {
                const fp = domain.matrix.fp;
                const db = domain.matrix.db;
                const ym = domain.matrix.ym;
                const ce = domain.matrix.ce;

                const baseCellClasses =
                  'inline-flex items-center justify-center min-w-[2.2rem] px-2 py-1 rounded-full text-[0.72rem] font-semibold border transition';

                const getClasses = (ownerKey: 'fp' | 'db' | 'ym' | 'ce', value: string | undefined) => {
                  if (!value) return 'text-slate-500';
                  const isSelected =
                    selectedCell &&
                    selectedCell.domainId === domain.id &&
                    selectedCell.ownerLabel === owners[ownerKey] &&
                    selectedCell.display === value;

                  const highlightColor =
                    ownerKey === 'fp'
                      ? 'border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/15'
                      : ownerKey === 'db'
                      ? 'border-indigo-500/60 text-indigo-200 hover:bg-indigo-500/15'
                      : ownerKey === 'ym'
                      ? 'border-rose-500/60 text-rose-200 hover:bg-rose-500/15'
                      : 'border-slate-500/60 text-slate-100 hover:bg-slate-500/10';

                  const selectedRing = isSelected ? ' ring-1 ring-offset-1 ring-offset-slate-950 ring-cyan-400' : '';

                  return `${baseCellClasses} ${highlightColor}${selectedRing}`;
                };

                const renderCell = (ownerKey: 'fp' | 'db' | 'ym' | 'ce', value: string | undefined) => {
                  if (!value) {
                    return <span className="text-slate-600 text-[0.7rem]">—</span>;
                  }

                  return (
                    <button
                      type="button"
                      onClick={() => handleClick(domain.id, domain.label, ownerKey, value)}
                      className={getClasses(ownerKey, value)}
                    >
                      {value}
                    </button>
                  );
                };

                return (
                  <tr key={domain.id} className="border-t border-slate-800/80 hover:bg-slate-900/40">
                    <td className="py-2 px-3 align-top">
                      <div className="space-y-0.5">
                        <p className="font-medium text-slate-100">{domain.label}</p>
                        <p className="text-[0.7rem] text-slate-300/85">{domain.subtitle}</p>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center align-middle">{renderCell('fp', fp)}</td>
                    <td className="py-2 px-3 text-center align-middle">{renderCell('db', db)}</td>
                    <td className="py-2 px-3 text-center align-middle">{renderCell('ym', ym)}</td>
                    <td className="py-2 px-3 text-center align-middle">{renderCell('ce', ce)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selectedCell && (
          <div className="mt-4 grid md:grid-cols-[1.2fr,1.1fr] gap-4 items-start">
            <div className="bg-slate-900/85 border border-cyan-500/50 rounded-xl p-4 space-y-2">
              <p className="text-[0.7rem] uppercase tracking-wide text-cyan-300 font-semibold mb-1">
                Domaine sélectionné
              </p>
              <h4 className="text-sm md:text-base font-bold text-white flex items-start gap-2">
                <Shield className="w-4 h-4 text-cyan-300 mt-[2px]" />
                <span>{selectedCell.domainLabel}</span>
              </h4>
              <p className="text-[0.75rem] text-slate-200/90 mt-1">
                {domains.find((d) => d.id === selectedCell.domainId)?.risks}
              </p>
            </div>

            <div className="bg-slate-900/85 border border-slate-500/60 rounded-xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[0.7rem] uppercase tracking-wide text-slate-300 font-semibold mb-1">
                    {selectedCell.ownerLabel}
                  </p>
                  <p className="text-xs text-slate-200/90">
                    Rôle dans ce domaine :{' '}
                    <span className="font-semibold text-cyan-200">{selectedCell.display}</span>
                  </p>
                </div>
              </div>

              <div className="mt-2 space-y-1.5 text-[0.75rem] text-slate-100/95">
                <p className="font-semibold text-slate-100 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center rounded-full bg-slate-800 px-2 py-0.5 text-[0.7rem] border border-slate-600">
                    {codeMeta[selectedCell.code].label}
                  </span>
                </p>
                <p>{codeMeta[selectedCell.code].description}</p>
              </div>

              <div className="mt-2 rounded-lg bg-red-900/25 border border-red-500/60 px-3 py-2 text-[0.73rem] text-red-100 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-[2px] text-red-300" />
                <div>
                  <p className="font-semibold mb-0.5">Risque si ce rôle est mal tenu</p>
                  <p>{codeMeta[selectedCell.code].risk}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

